import React from 'react';
import type { ExecutionStatus, OptimizerChipState } from '@chatsdet/ui';
import type {
  ActionDataRequirement,
  ActionInputValues,
  WorkflowInputDefinition,
} from '@chatsdet/types';
import type { WorkflowNode } from '../../../../types/workflow';
import type { AvailableSandboxAction } from '../../../../hooks/useAvailableSandboxActions';
import type { SeedDataOutput } from '../../../../hooks/useSeedWorkflowOutputs';
import type { EffectivePlaybookData } from '../types/playbook-cache';
import { deriveLoginNodeLabels } from '../deriveLoginNodeLabels';
import { deriveApiSequenceStepLabels } from '../deriveApiSequenceStepLabels';
import { deriveSandboxActionSensitivity } from '../deriveSandboxActionSensitivity';
import { issuesForNode } from './issuesForNode';
import type { OpenWorkflowIssueSummary } from '../../flow-list/types';

type UseCanvasNodesParams = {
  nodes: WorkflowNode[];
  credentials: any[];
  credentialPropertyMap: Map<string, string | null>;
  seedOutputsByWorkflowId?: Map<string, SeedDataOutput[]>;
  templateNameById?: Map<string, string>;
  workflowInputs?: WorkflowInputDefinition[];
  apiRequests: any[];
  sandboxActions: AvailableSandboxAction[];
  nodeStatuses: Map<string, any>;
  /** Live cache-optimizer chip state per node (separate axis from execution). */
  optimizerStatuses?: Map<string, OptimizerChipState>;
  effectivePlaybooks?: Map<string, EffectivePlaybookData>;
  currentStepId: string | null;
  historicalFailedNodeId: string | null;
  flowValidation: {
    isValidationActive: boolean;
    validationResult: { errorsByNode: Map<string, any> } | null;
  };
  events: any[];
  setNodes: (fn: (prev: WorkflowNode[]) => WorkflowNode[]) => void;
  /** Open issues linked to this workflow (see `useWorkflowOpenIssues`) — powers each node's issue pill. */
  openIssues: OpenWorkflowIssueSummary[];
};

export function useCanvasNodes({
  nodes,
  credentials,
  credentialPropertyMap,
  seedOutputsByWorkflowId,
  templateNameById,
  workflowInputs,
  apiRequests,
  sandboxActions,
  nodeStatuses,
  optimizerStatuses,
  effectivePlaybooks,
  currentStepId,
  historicalFailedNodeId,
  flowValidation,
  events,
  setNodes,
  openIssues,
}: UseCanvasNodesParams) {
  const processedEventsRef = React.useRef(0);

  const enrichedNodes = React.useMemo(() => {
    const withLoginLabels = deriveLoginNodeLabels({
      nodes,
      credentials,
      credentialPropertyMap,
      seedOutputsByWorkflowId,
      templateNameById,
      workflowInputs,
    });
    return deriveApiSequenceStepLabels({ nodes: withLoginLabels, apiRequests });
  }, [
    nodes,
    credentials,
    credentialPropertyMap,
    seedOutputsByWorkflowId,
    templateNameById,
    workflowInputs,
    apiRequests,
  ]);

  const canvasNodes = React.useMemo(() => {
    const withSandboxActionSensitivity = deriveSandboxActionSensitivity({ nodes: enrichedNodes, sandboxActions });
    return withSandboxActionSensitivity.map((node) => {
      const statusInfo = nodeStatuses.get(node.id);
      const playbook = effectivePlaybooks?.get(node.id);
      let executionStatus: ExecutionStatus = statusInfo?.state ?? 'idle';

      // Overlay historical failed status when viewing a failed run
      if (historicalFailedNodeId === node.id && executionStatus === 'idle') {
        executionStatus = 'failed';
      }

      return {
        ...node,
        data: {
          ...node.data,
          ...(playbook
            ? {
                playbookSteps: playbook.steps,
                playbookStepCount: playbook.steps.length || undefined,
                hasCachedReplay: playbook.activeCache !== null,
                cacheRequiresAgent:
                  playbook.activeCache !== null &&
                  (!playbook.activeCache.isValid ||
                    (playbook.activeCache.compiledEntries?.some((entry) => entry.type === 'gap') ??
                      false)),
                cachedAt: playbook.activeCache?.createdAt ?? null,
              }
            : {}),
          executionStatus,
          optimizerStatus: optimizerStatuses?.get(node.id),
          executionMessage: statusInfo?.message,
          isExecuting: currentStepId === node.id,
          conditionResult: statusInfo?.conditionResult,
          resumeAt: statusInfo?.resumeAt,
          delaySeconds: statusInfo?.delaySeconds ?? node.data?.delaySeconds,
          hasValidationError:
            executionStatus === 'idle' &&
            flowValidation.isValidationActive &&
            (flowValidation.validationResult?.errorsByNode.has(node.id) ?? false),
          openIssues: openIssues.length ? issuesForNode(node, openIssues) : undefined,
        },
      };
    });
  }, [
    enrichedNodes,
    sandboxActions,
    nodeStatuses,
    optimizerStatuses,
    effectivePlaybooks,
    currentStepId,
    historicalFailedNodeId,
    flowValidation.isValidationActive,
    flowValidation.validationResult,
    openIssues,
  ]);

  // Merge data requirements emitted during execution back into node state
  React.useEffect(() => {
    if (events.length < processedEventsRef.current) {
      processedEventsRef.current = 0;
    }

    if (events.length <= processedEventsRef.current) {
      return;
    }

    for (let i = processedEventsRef.current; i < events.length; i += 1) {
      const event = events[i];

      if (
        event.type === 'step-failed' ||
        event.type === 'step-progress' ||
        event.type === 'step-completed'
      ) {
        const {
          nodeId,
          dataRequirements,
          inputValues,
          requirementsUpdatedAt,
          guidanceImprovedAt,
          previousActionDescription,
          actionDescription,
          playbookSteps,
          playbookStepsUpdatedAt,
        } = event.data as {
          nodeId: string;
          dataRequirements?: ActionDataRequirement[];
          inputValues?: ActionInputValues;
          requirementsUpdatedAt?: string;
          guidanceImprovedAt?: string;
          previousActionDescription?: string;
          actionDescription?: string;
          playbookSteps?: unknown[];
          playbookStepsUpdatedAt?: string;
        };

        // Only merge non-empty values to avoid overwriting user-configured
        // fields with empty arrays/objects from early agent reports.
        const hasNonEmptyReqs = Array.isArray(dataRequirements) && dataRequirements.length > 0;
        const hasNonEmptyInputs =
          inputValues !== undefined && inputValues !== null && Object.keys(inputValues).length > 0;

        const hasUpdate =
          hasNonEmptyReqs ||
          hasNonEmptyInputs ||
          requirementsUpdatedAt !== undefined ||
          guidanceImprovedAt !== undefined ||
          (Array.isArray(playbookSteps) && playbookSteps.length > 0);

        if (!hasUpdate) {
          continue;
        }

        setNodes((prev) =>
          prev.map((node) =>
            node.id === nodeId
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    ...(hasNonEmptyReqs ? { dataRequirements } : {}),
                    ...(hasNonEmptyInputs ? { inputValues } : {}),
                    ...(requirementsUpdatedAt !== undefined
                      ? { lastRequirementUpdateAt: requirementsUpdatedAt }
                      : {}),
                    ...(guidanceImprovedAt !== undefined
                      ? { guidanceImprovedAt, previousActionDescription, actionDescription }
                      : {}),
                    ...(Array.isArray(playbookSteps) && playbookSteps.length > 0
                      ? { playbookSteps, playbookStepsUpdatedAt }
                      : {}),
                  },
                }
              : node
          )
        );
      }
    }

    processedEventsRef.current = events.length;
  }, [events, setNodes]);

  return {
    enrichedNodes,
    canvasNodes,
  };
}
